/**
 * Recently Viewed Products Popup
 * Shows a bottom bar with recently viewed products
 */

if (!customElements.get('recently-viewed-popup')) {
  class RecentlyViewedPopup extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'rv:handles'; // Use existing tracking system
      this.dismissalKey = 'jnco:recently-viewed-dismissed';
      this.isVisible = false;
    }

    connectedCallback() {
      this.delay = parseInt(this.dataset.delay || '3000');
      this.maxProducts = parseInt(this.dataset.maxProducts || '4');
      this.homepageOnly = this.dataset.homepageOnly === 'true';
      this.currentProductHandle = this.dataset.currentProductHandle;
      this.enableQuickAdd = this.dataset.enableQuickAdd === 'true';
      this.imageRatio = this.dataset.imageRatio || 'adapt';
      this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';
      this.showVendor = this.dataset.showVendor === 'true';
      this.grid = this.querySelector('#recently-viewed-grid');

      // Check if should show
      if (!this.shouldShow()) {
        this.remove();
        return;
      }

      // Create overlay element
      this.createOverlay();

      // Setup close button
      const closeBtn = this.querySelector('.recently-viewed-popup__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.dismiss());
      }

      // Close on overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.dismiss());
      }

      // Close when clicking outside the content area
      this.addEventListener('click', (e) => {
        if (e.target === this) {
          this.dismiss();
        }
      });

      // Prevent clicks inside the container from closing
      const container = this.querySelector('.recently-viewed-popup__container');
      if (container) {
        container.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }

      // Close on Escape key
      this.handleEscape = (e) => {
        if (e.key === 'Escape' && this.isVisible) {
          this.dismiss();
        }
      };
      document.addEventListener('keydown', this.handleEscape);

      // Fetch and render products, then show after delay
      this.fetchAndRenderProducts().then(() => {
        setTimeout(() => {
          this.show();
        }, this.delay);
      });
    }

    createOverlay() {
      // Create overlay if it doesn't exist
      if (!document.querySelector('.recently-viewed-popup-overlay')) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'recently-viewed-popup-overlay';
        this.overlay.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.overlay);
      } else {
        this.overlay = document.querySelector('.recently-viewed-popup-overlay');
      }
    }

    shouldShow() {
      // Check if homepage only setting
      if (this.homepageOnly && !document.body.classList.contains('template-index')) {
        return false;
      }

      // Check if dismissed
      if (this.wasDismissed()) {
        return false;
      }

      // Check if we have products to show
      const products = this.getProductsToShow();
      return products.length > 0;
    }

    getProductsToShow() {
      let handles = this.getRecentlyViewedHandles();

      // Filter out current product handle if on product page
      if (this.currentProductHandle) {
        handles = handles.filter(h => h !== this.currentProductHandle);
      }

      // Limit to max products
      return handles.slice(0, this.maxProducts);
    }

    getRecentlyViewedHandles() {
      try {
        const data = localStorage.getItem(this.storageKey);
        if (!data) return [];
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('Failed to retrieve recently viewed products:', e);
        return [];
      }
    }

    wasDismissed() {
      try {
        const dismissed = sessionStorage.getItem(this.dismissalKey);
        return dismissed === 'true';
      } catch (e) {
        return false;
      }
    }

    show() {
      // Store currently focused element to restore later
      this.previouslyFocusedElement = document.activeElement;

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('recently-viewed-popup-overlay--visible');
      }

      this.classList.add('recently-viewed-popup--visible');
      this.isVisible = true;

      // Set up focus trap
      this.setupFocusTrap();

      // Focus the close button
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('.recently-viewed-popup__close');
        if (closeBtn) {
          closeBtn.focus();
        }
      });
    }

    dismiss() {
      this.classList.remove('recently-viewed-popup--visible');
      this.isVisible = false;

      // Hide overlay
      if (this.overlay) {
        this.overlay.classList.remove('recently-viewed-popup-overlay--visible');
      }

      // Remove focus trap
      this.removeFocusTrap();

      // Restore focus to previously focused element
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }

      // Remember dismissal for session
      try {
        sessionStorage.setItem(this.dismissalKey, 'true');
      } catch (e) {
        console.warn('Failed to save dismissal state:', e);
      }

      // Remove from DOM after animation
      setTimeout(() => {
        this.remove();
        if (this.overlay) {
          this.overlay.remove();
        }
      }, 300);
    }

    setupFocusTrap() {
      // Get all focusable elements within the popup
      this.handleFocusTrap = (e) => {
        if (!this.isVisible) return;

        const focusableElements = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Trap focus within the popup
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', this.handleFocusTrap);
    }

    removeFocusTrap() {
      if (this.handleFocusTrap) {
        document.removeEventListener('keydown', this.handleFocusTrap);
      }
      if (this.handleEscape) {
        document.removeEventListener('keydown', this.handleEscape);
      }
    }

    async fetchAndRenderProducts() {
      const handles = this.getProductsToShow();
      if (!handles.length) return;

      // Fetch full product data from Shopify
      const productPromises = handles.map(handle => this.fetchProduct(handle));
      const products = await Promise.all(productPromises);

      // Filter out any failed fetches
      const validProducts = products.filter(p => p !== null);

      // Render products
      if (validProducts.length > 0) {
        this.renderProducts(validProducts);
      } else {
        this.remove();
      }
    }

    async fetchProduct(handle) {
      try {
        // Try custom view first (templates/product.carddata.liquid includes metafields)
        let response = await fetch(`/products/${handle}?view=carddata`);

        if (response.ok) {
          try {
            const text = await response.text();
            // Try to parse as JSON
            const product = JSON.parse(text.trim());

            // Log first product to debug metafield
            if (!this._loggedMetafield) {
              console.log('✓ Using carddata view');
              console.log('Product data:', product);
              console.log('Metafield:', product.metafields);
              this._loggedMetafield = true;
            }

            return product;
          } catch (e) {
            console.log(`Carddata view exists but couldn't parse JSON, falling back to .js`);
          }
        }

        // Fall back to standard .js endpoint
        console.log(`Using standard .js for ${handle}`);
        response = await fetch(`/products/${handle}.js`);

        if (!response.ok) throw new Error('Product not found');
        return await response.json();
      } catch (error) {
        console.warn(`Failed to fetch product ${handle}:`, error);
        return null;
      }
    }

    renderProducts(products) {
      // Render product cards
      const cardsHTML = products.map(product => this.renderProductCard(product)).join('');

      // Render hidden quick add forms/modals for multi-variant products
      const modalsHTML = this.enableQuickAdd
        ? products.map(product => this.renderQuickAddModal(product)).join('')
        : '';

      this.grid.innerHTML = cardsHTML + modalsHTML;

      // Initialize card-product-quick-add functionality if script is loaded
      if (this.enableQuickAdd && typeof window.initCardQuickAdd === 'function') {
        window.initCardQuickAdd(this.grid);
      }
    }

    renderQuickAddModal(product) {
      if (product.variants.length === 1) {
        // Single variant - needs hidden form
        const formId = `quick-add-popup-${product.id}`;
        const variant = product.variants[0];
        return `
          <product-form data-section-id="recently-viewed-popup" style="display:none;">
            <form method="post" action="/cart/add" id="${formId}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="id" value="${variant.id}" class="product-variant-id">
              <button
                id="${formId}-submit"
                type="submit"
                name="add"
                class="quick-add__submit button button--full-width button--secondary"
                aria-haspopup="dialog"
                aria-labelledby="${formId}-submit"
                aria-live="polite"
                data-sold-out-message="true"
              >
                <span>Add to cart</span>
                <div class="loading__spinner hidden">
                  <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </form>
          </product-form>
        `;
      } else {
        // Multi-variant - needs modal
        const formId = `quick-add-popup-${product.id}`;
        return `
          <quick-add-modal id="QuickAdd-${product.id}" class="quick-add-modal">
            <div class="mobile" style="display: flex; align-items: center; height: 4.25rem;">
              <div style="padding-left: 2rem;">QUICK ADD</div>
              <button
                id="ModalClose-${product.id}"
                type="button"
                class="quick-add-modal__toggle"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                  <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <div role="dialog" aria-label="Choose options for ${this.escapeHtml(product.title)}" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">
              <div class="desktop" style="display: flex; justify-content: space-between; align-items: center; padding: 1rem 2rem 1rem; border-bottom: 0.1rem solid rgba(0,0,0,0.1);">
                <div style="font-size: 1.6rem; font-weight: 600;">QUICK ADD</div>
                <button id="ModalDClose-${product.id}" type="button" class="quick-add-modal__toggle" aria-label="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                    <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <div id="QuickAddInfo-${product.id}" class="quick-add-modal__content-info"></div>
            </div>
          </quick-add-modal>
        `;
      }
    }

    renderProductCard(product) {
      const variant = product.variants[0];
      const imageUrl = product.featured_image || (product.images && product.images[0]);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const isPreorder = product.tags && product.tags.some(tag => tag.toLowerCase().includes('pre-order') || tag.toLowerCase() === 'preorder');
      const showBadge = product.tags && (product.tags.includes('SALE') || product.tags.includes('sale'));

      // Use custom metafield for card title if available, fallback to product title
      const cardTitle = product.metafields?.custom?.product_card_title?.value || product.title;

      return `
        <div class="card-wrapper product-card-wrapper underline-links-hover" data-product-handle="${product.handle}">
          <div class="card card--standard card--media card--shape">
            <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: ${this.getRatioPercent(product)};">
              <div class="card__media">
                ${showBadge ? '<span class="badge badge--bottom-left color-accent-2">SALE</span>' : ''}
                ${isPreorder ? '<span class="card__tag-badge card__tag-badge--preorder">PRE-ORDER</span>' : ''}
                ${this.renderWishlistButton(product)}
                <div class="media media--transparent media--hover-effect">
                  <a href="/products/${product.handle}">
                    ${imageUrl ? `
                      <img
                        srcset="${this.getImageUrl(imageUrl, 300)} 300w, ${this.getImageUrl(imageUrl, 600)} 600w"
                        src="${this.getImageUrl(imageUrl, 400)}"
                        sizes="(min-width: 750px) calc((100vw - 10rem) / 4), calc(50vw - 3rem)"
                        alt="${this.escapeHtml(cardTitle)}"
                        loading="lazy"
                        class="motion-reduce"
                        width="400"
                        height="${this.getImageHeight(400, product)}"
                      >
                    ` : ''}
                  </a>
                </div>
                ${this.enableQuickAdd ? this.renderQuickAddIcon(product, variant) : ''}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading${product.available ? '' : ' card__heading--placeholder'}">
                  <a href="/products/${product.handle}" class="full-unstyled-link">
                    ${this.escapeHtml(cardTitle)}
                  </a>
                </h3>
                ${this.showVendor && product.vendor ? `
                  <div class="caption-with-letter-spacing light">${this.escapeHtml(product.vendor)}</div>
                ` : ''}
                <div class="card-information">
                  ${this.renderPrice(variant, onSale)}
                </div>
                ${this.renderSwatches(product)}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderPrice(variant, onSale) {
      return `
        <div class="price ${onSale ? 'price--on-sale' : ''}">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">
                ${this.formatMoney(variant.price)}
              </span>
            </div>
            ${onSale ? `
              <div class="price__sale">
                <span class="visually-hidden visually-hidden--inline">Sale price</span>
                <span class="price-item price-item--sale price-item--last">
                  ${this.formatMoney(variant.price)}
                </span>
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
                <s class="price-item price-item--regular">
                  ${this.formatMoney(variant.compare_at_price)}
                </s>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }

    renderWishlistButton(product) {
      return `
        <button
          type="button"
          aria-label="Add to wishlist"
          class="wishlist-button icon-position-top-right"
          button-wishlist
          data-product-handle="${product.handle}"
        >
          <svg class="icon icon-heart" viewBox="0 0 290 256" width="25" height="25" xmlns="http://www.w3.org/2000/svg">
            <path d="M258.844192 127.790368L145 241.63456 31.1558082 127.790368c-26.9461761-26.946176-26.9461761-70.6345598 0-97.5807359 26.9461762-26.94617613 70.6345598-26.94617613 97.5807358 0L145 46.4730881l16.263456-16.263456c26.946176-26.94617613 70.63456-26.94617613 97.580736 0 26.946176 26.9461761 26.946176 70.6345599 0 97.5807359z" stroke="#000" stroke-width="20" fill-rule="evenodd"/>
          </svg>
        </button>
      `;
    }

    renderQuickAddIcon(product, variant) {
      const formId = `quick-add-popup-${product.id}`;
      const isAvailable = variant && variant.available;

      if (product.variants.length === 1 && isAvailable) {
        // Single variant - direct add to cart with icon
        return `
          <button
            type="button"
            id="quickAdd${product.id}"
            data-quick-add-target="${formId}-submit"
            aria-label="Quick add ${this.escapeHtml(product.title)}"
            style="background: transparent; border: none; padding: 0; position: absolute; z-index: 2; bottom: 1.5rem; right: 1.5rem; cursor: pointer;"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="quick-add-modal__icon">
              <g clip-path="url(#clip0_2823_746)">
                <path d="M2.6167 8.86743L10.1167 8.86743" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/>
                <path d="M13.1167 8.86743L14.6167 8.86743" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/>
                <path d="M2.6167 1.86743H6.32275L7.21938 5.57349M8.77808 12.3674C9.27808 14.8674 10.6832 14.8474 11.1167 14.8386H19.2939C19.7275 14.8474 20.1503 14.7038 20.4889 14.4329C20.8275 14.1621 21.0603 13.7811 21.147 13.3562L23 5.57349L7.21938 5.57349M7.21938 5.57349L7.89481 8.42928" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <path d="M9.6167 22.1326C10.7213 22.1326 11.6167 21.2371 11.6167 20.1326C11.6167 19.028 10.7213 18.1326 9.6167 18.1326C8.51213 18.1326 7.6167 19.028 7.6167 20.1326C7.6167 21.2371 8.51213 22.1326 9.6167 22.1326Z" fill="white"/>
                <path d="M20.6167 22.1326C21.7213 22.1326 22.6167 21.2371 22.6167 20.1326C22.6167 19.028 21.7213 18.1326 20.6167 18.1326C19.5121 18.1326 18.6167 19.028 18.6167 20.1326C18.6167 21.2371 19.5121 22.1326 20.6167 22.1326Z" fill="white"/>
                <path d="M0.616699 12.3674H9.6167" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </g>
              <defs><clipPath id="clip0_2823_746"><rect width="24" height="24" fill="white"/></clipPath></defs>
            </svg>
            <div class="loading__spinner hidden quick-add-trigger__spinner">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        `;
      } else if (product.variants.length > 1) {
        // Multiple variants - icon that opens modal via modal-opener
        return `
          <modal-opener data-modal="#QuickAdd-${product.id}" style="position: absolute; z-index: 2; bottom: .5rem; right: 1.5rem; display: inline-block;">
            <button
              type="button"
              id="quickAdd${product.id}"
              data-product-url="/products/${product.handle}"
              aria-label="Quick add ${this.escapeHtml(product.title)}"
              style="background: transparent; border: none; padding: 0; cursor: pointer;"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="quick-add-modal__icon">
                <g clip-path="url(#clip0_2823_746)">
                  <path d="M2.6167 8.86743L10.1167 8.86743" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/>
                  <path d="M13.1167 8.86743L14.6167 8.86743" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/>
                  <path d="M2.6167 1.86743H6.32275L7.21938 5.57349M8.77808 12.3674C9.27808 14.8674 10.6832 14.8474 11.1167 14.8386H19.2939C19.7275 14.8474 20.1503 14.7038 20.4889 14.4329C20.8275 14.1621 21.0603 13.7811 21.147 13.3562L23 5.57349L7.21938 5.57349M7.21938 5.57349L7.89481 8.42928" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                  <path d="M9.6167 22.1326C10.7213 22.1326 11.6167 21.2371 11.6167 20.1326C11.6167 19.028 10.7213 18.1326 9.6167 18.1326C8.51213 18.1326 7.6167 19.028 7.6167 20.1326C7.6167 21.2371 8.51213 22.1326 9.6167 22.1326Z" fill="white"/>
                  <path d="M20.6167 22.1326C21.7213 22.1326 22.6167 21.2371 22.6167 20.1326C22.6167 19.028 21.7213 18.1326 20.6167 18.1326C19.5121 18.1326 18.6167 19.028 18.6167 20.1326C18.6167 21.2371 19.5121 22.1326 20.6167 22.1326Z" fill="white"/>
                  <path d="M0.616699 12.3674H9.6167" stroke="white" stroke-width="2" stroke-linecap="round"/>
                </g>
                <defs><clipPath id="clip0_2823_746"><rect width="24" height="24" fill="white"/></clipPath></defs>
              </svg>
              <div class="loading__spinner hidden quick-add-trigger__spinner">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </button>
          </modal-opener>
        `;
      }
      return '';
    }

    renderSwatches(product) {
      // Find color option from product options
      const colorOption = product.options.find((opt, idx) => {
        const optName = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
        return optName === 'color' || optName === 'colour' || optName.includes('wash');
      });

      if (!colorOption) return '';

      // Get option index
      const optionIndex = product.options.findIndex((opt, idx) => {
        const optName = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
        return optName === 'color' || optName === 'colour' || optName.includes('wash');
      });

      if (optionIndex === -1) return '';

      // Get unique color values from variants
      const colorValues = [...new Set(product.variants.map(v => v.options[optionIndex]))].filter(Boolean).slice(0, 6);

      if (colorValues.length === 0) return '';

      return `
        <div class="card__swatches" role="list" aria-label="Available colors for ${this.escapeHtml(product.title)}">
          ${colorValues.map(color => `
            <span class="swatch-link" role="listitem" title="${this.escapeHtml(color)}" data-swatch-label="${this.escapeHtml(color)}">
              <span class="swatch swatch--square" style="--swatch--size: 1.6rem;"></span>
            </span>
          `).join('')}
        </div>
      `;
    }

    getRatioPercent(product) {
      if (this.imageRatio === 'portrait') return '125%';
      if (this.imageRatio === 'square') return '100%';

      // For adapt, try to get aspect ratio from various sources
      if (this.imageRatio === 'adapt') {
        // Try featured_media first
        if (product.featured_media && product.featured_media.preview_image) {
          const preview = product.featured_media.preview_image;
          if (preview.aspect_ratio) {
            return `${(1 / preview.aspect_ratio) * 100}%`;
          }
        }

        // Try media if available
        if (product.media && product.media[0]) {
          const media = product.media[0];
          if (media.aspect_ratio) {
            return `${(1 / media.aspect_ratio) * 100}%`;
          }
          if (media.preview_image && media.preview_image.aspect_ratio) {
            return `${(1 / media.preview_image.aspect_ratio) * 100}%`;
          }
        }
      }

      // Fallback to portrait for more natural look
      return '125%';
    }

    getImageUrl(image, width) {
      if (typeof image === 'string') {
        // Shopify CDN URL format
        return image.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${width}x.$1`);
      }
      return image;
    }

    getImageHeight(width, product) {
      if (this.imageRatio === 'portrait') return Math.round(width * 1.25);
      if (this.imageRatio === 'square') return width;

      // For adapt, try to get aspect ratio from various sources
      if (this.imageRatio === 'adapt') {
        // Try featured_media first
        if (product.featured_media && product.featured_media.preview_image) {
          const preview = product.featured_media.preview_image;
          if (preview.aspect_ratio) {
            return Math.round(width / preview.aspect_ratio);
          }
        }

        // Try media if available
        if (product.media && product.media[0]) {
          const media = product.media[0];
          if (media.aspect_ratio) {
            return Math.round(width / media.aspect_ratio);
          }
          if (media.preview_image && media.preview_image.aspect_ratio) {
            return Math.round(width / media.preview_image.aspect_ratio);
          }
        }
      }

      // Fallback to portrait for more natural look
      return Math.round(width * 1.25);
    }

    formatMoney(cents) {
      const docBody = document.body;
      const currency = (docBody && docBody.dataset && docBody.dataset.currency) || 'USD';
      const locale = (docBody && docBody.dataset && docBody.dataset.locale) || (document.documentElement.lang || 'en');

      try {
        return new Intl.NumberFormat(locale || 'en-US', {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format((cents || 0) / 100);
      } catch (e) {
        return `$${((cents || 0) / 100).toFixed(2)}`;
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define('recently-viewed-popup', RecentlyViewedPopup);
}
